import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/loading-state';
import { billingApi, type PaymentRun } from '@/lib/api/billing';
import { RUN_STATUS, formatCents } from '@/lib/billing';

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const iso = (tag: Date) =>
  `${tag.getFullYear()}-${String(tag.getMonth() + 1).padStart(2, '0')}-${String(tag.getDate()).padStart(2, '0')}`;

/**
 * Zahlungsläufe eines Monats im Kalender (T-0134): jeder Lauf an seinem
 * Einzugstag, oben die Summen. Ein Klick öffnet den Lauf wie in der Liste.
 */
export default function ZahlungslaeufeMonat({
  status,
  neuLaden,
  onOeffnen,
}: Readonly<{ status?: string; neuLaden: number; onOeffnen: (run: PaymentRun) => void }>) {
  const [monat, setMonat] = useState(() => {
    const heute = new Date();
    return new Date(heute.getFullYear(), heute.getMonth(), 1);
  });
  const [laeufe, setLaeufe] = useState<PaymentRun[]>([]);
  const [laedt, setLaedt] = useState(true);

  const erster = monat;
  const letzter = useMemo(() => new Date(monat.getFullYear(), monat.getMonth() + 1, 0), [monat]);

  useEffect(() => {
    let aktuell = true;
    setLaedt(true);
    billingApi
      .listRuns({ status, von: iso(erster), bis: iso(letzter), limit: 200 })
      .then((antwort) => {
        if (aktuell) setLaeufe(Array.isArray(antwort?.items) ? antwort.items : []);
      })
      .catch((fehler) => {
        if (aktuell) {
          setLaeufe([]);
          toast.error(fehler instanceof Error ? fehler.message : 'Zahlungsläufe konnten nicht geladen werden');
        }
      })
      .finally(() => aktuell && setLaedt(false));
    return () => {
      aktuell = false;
    };
  }, [erster, letzter, status, neuLaden]);

  const jeTag = useMemo(() => {
    const karte = new Map<string, PaymentRun[]>();
    for (const lauf of laeufe) {
      const tag = String(lauf.collectionDate || '').slice(0, 10);
      karte.set(tag, [...(karte.get(tag) ?? []), lauf]);
    }
    return karte;
  }, [laeufe]);

  const summen = useMemo(() => {
    const aktiv = laeufe.filter((l) => l.status !== 'cancelled');
    const summe = (liste: PaymentRun[]) => liste.reduce((s, l) => s + (l.totalCents || 0), 0);
    return {
      anzahl: aktiv.length,
      gesamt: summe(aktiv),
      abgeschlossen: summe(aktiv.filter((l) => l.status === 'settled')),
      offen: summe(aktiv.filter((l) => l.status !== 'settled')),
    };
  }, [laeufe]);

  // Raster ab Montag der ersten Woche.
  const zellen = useMemo(() => {
    const vorlauf = (erster.getDay() + 6) % 7;
    const tage: Array<Date | null> = Array.from({ length: vorlauf }, () => null);
    for (let d = 1; d <= letzter.getDate(); d += 1) {
      tage.push(new Date(monat.getFullYear(), monat.getMonth(), d));
    }
    while (tage.length % 7 !== 0) tage.push(null);
    return tage;
  }, [erster, letzter, monat]);

  const heute = iso(new Date());
  const titel = monat.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label="Vormonat"
            onClick={() => setMonat(new Date(monat.getFullYear(), monat.getMonth() - 1, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="w-40 text-center font-medium">{titel}</div>
          <Button
            variant="outline"
            size="sm"
            aria-label="Folgemonat"
            onClick={() => setMonat(new Date(monat.getFullYear(), monat.getMonth() + 1, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            <span className="text-muted-foreground">Läufe </span>
            <span className="tabular-nums font-medium">{summen.anzahl}</span>
          </span>
          <span>
            <span className="text-muted-foreground">Summe </span>
            <span className="tabular-nums font-medium">{formatCents(summen.gesamt)}</span>
          </span>
          <span>
            <span className="text-muted-foreground">abgeschlossen </span>
            <span className="tabular-nums font-medium">{formatCents(summen.abgeschlossen)}</span>
          </span>
          <span>
            <span className="text-muted-foreground">noch offen </span>
            <span className="tabular-nums font-medium">{formatCents(summen.offen)}</span>
          </span>
        </div>
      </div>

      {laedt ? (
        <LoadingState message="Zahlungsläufe werden geladen …" />
      ) : (
        <div className="overflow-x-auto">
          <div className="grid min-w-[720px] grid-cols-7 gap-px overflow-hidden rounded-lg border bg-border">
            {WOCHENTAGE.map((tag) => (
              <div key={tag} className="bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                {tag}
              </div>
            ))}
            {zellen.map((tag, index) => {
              const schluessel = tag ? iso(tag) : `leer-${index}`;
              const liste = tag ? jeTag.get(iso(tag)) ?? [] : [];
              return (
                <div
                  key={schluessel}
                  className={`min-h-24 bg-background p-1.5 ${tag && iso(tag) === heute ? 'ring-1 ring-inset ring-primary' : ''}`}
                >
                  {tag ? <div className="text-xs text-muted-foreground">{tag.getDate()}</div> : null}
                  <div className="mt-1 space-y-1">
                    {liste.map((lauf) => (
                      <button
                        key={lauf.id}
                        type="button"
                        onClick={() => onOeffnen(lauf)}
                        className="w-full rounded border bg-card px-1.5 py-1 text-left text-xs hover:bg-muted"
                        title={`${lauf.runNumber} · ${lauf.itemCount} Aufträge`}
                      >
                        <div className="truncate font-medium tabular-nums">{lauf.runNumber}</div>
                        <div className="flex items-center justify-between gap-1">
                          <span className="tabular-nums">{formatCents(lauf.totalCents)}</span>
                          <Badge variant={RUN_STATUS[lauf.status]?.variant ?? 'outline'} size="sm">
                            {RUN_STATUS[lauf.status]?.label ?? lauf.status}
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
